import { useState, useEffect } from 'react';
import { Brain, Loader2, ChevronRight, Sparkles, Utensils, RefreshCw, TrendingUp, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

const CUISINE_OPTIONS = [
  { id: 'south_african', name: 'South African', description: 'Local favourites like bunny chow, bobotie, and braai dishes', emoji: '🇿🇦' },
  { id: 'indian', name: 'Indian', description: 'Curries, biryanis, and tandoori dishes', emoji: '🍛' },
  { id: 'mediterranean', name: 'Mediterranean', description: 'Greek, Turkish, and Middle Eastern flavours', emoji: '🫒' },
  { id: 'asian', name: 'Asian', description: 'Chinese, Thai, and Malaysian influences', emoji: '🥢' },
  { id: 'comfort', name: 'Comfort Food', description: 'Hearty, filling dishes for hungry workers', emoji: '🍲' },
  { id: 'healthy', name: 'Healthy Options', description: 'Lighter, nutritious meals', emoji: '🥗' },
  { id: 'vegetarian', name: 'Vegetarian', description: 'Meat-free options', emoji: '🥬' },
];

export default function AdminMenuAISuggestions() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [orderAnalysis, setOrderAnalysis] = useState(null);
  
  // Quick Add state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Main Course',
    menu_type: 'daily_meals',
    ingredients: ''
  });
  const [addingItem, setAddingItem] = useState(false);

  const generateSuggestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/menu/ai-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cuisine_focus: selectedCuisine?.name || null,
          include_order_analysis: true,
          max_suggestions: 5
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate suggestions');
      }

      const data = await response.json();
      
      if (data.error) {
        toast.error(data.error);
        return;
      }

      setSuggestions(data.suggestions);
      setOrderAnalysis(data.order_analysis);
      toast.success('AI suggestions generated successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  // Parse a suggestion from the text (helper to pre-fill form)
  const parseSuggestionForQuickAdd = (suggestionText) => {
    // Try to extract name and price from common patterns like "**Name** or "### Name"
    const nameMatch = suggestionText.match(/(?:\*\*|###?\s*)([^*\n]+?)(?:\*\*|\n|$)/);
    const priceMatch = suggestionText.match(/R\s*(\d+(?:[.,]\d{2})?)/);
    
    return {
      name: nameMatch ? nameMatch[1].trim() : '',
      price: priceMatch ? priceMatch[1].replace(',', '.') : ''
    };
  };

  // Open Quick Add dialog with pre-filled data
  const openQuickAdd = (suggestionBlock = null) => {
    if (suggestionBlock) {
      const parsed = parseSuggestionForQuickAdd(suggestionBlock);
      setQuickAddForm({
        name: parsed.name,
        description: '',
        price: parsed.price,
        category: 'Main Course',
        menu_type: 'daily_meals',
        ingredients: ''
      });
    } else {
      setQuickAddForm({
        name: '',
        description: '',
        price: '',
        category: 'Main Course',
        menu_type: 'daily_meals',
        ingredients: ''
      });
    }
    setQuickAddOpen(true);
  };

  // Submit new menu item
  const submitQuickAdd = async () => {
    if (!quickAddForm.name || !quickAddForm.price) {
      toast.error('Name and price are required');
      return;
    }

    setAddingItem(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/menu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: quickAddForm.name,
          description: quickAddForm.description,
          price: parseFloat(quickAddForm.price),
          category: quickAddForm.category,
          menu_type: quickAddForm.menu_type,
          is_available: true,
          is_halaal: true,
          is_loyalty_eligible: true,
          tags: quickAddForm.ingredients ? quickAddForm.ingredients.split(',').map(t => t.trim()) : []
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add item');
      }

      toast.success(`${quickAddForm.name} added to menu!`);
      setQuickAddOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAddingItem(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Brain className="h-7 w-7 text-primary" />
            AI Menu Suggestions
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Get AI-powered menu ideas based on your order history and popular cuisines
          </p>
        </div>
        <Button
          onClick={generateSuggestions}
          disabled={loading}
          className="bg-primary hover:bg-primary/90"
          data-testid="generate-suggestions-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Ideas
            </>
          )}
        </Button>
      </div>

      {/* Cuisine Selection */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg">Cuisine Focus (Optional)</CardTitle>
          <CardDescription>
            Select a cuisine style to focus the AI suggestions, or leave unselected for a mix
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {CUISINE_OPTIONS.map((cuisine) => (
              <button
                key={cuisine.id}
                onClick={() => setSelectedCuisine(selectedCuisine?.id === cuisine.id ? null : cuisine)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedCuisine?.id === cuisine.id
                    ? 'border-primary bg-primary/10 dark:bg-primary/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
                }`}
                data-testid={`cuisine-${cuisine.id}`}
              >
                <span className="text-2xl mb-2 block">{cuisine.emoji}</span>
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{cuisine.name}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                  {cuisine.description}
                </p>
              </button>
            ))}
          </div>
          {selectedCuisine && (
            <div className="mt-4 flex items-center gap-2 text-sm text-primary">
              <ChevronRight className="h-4 w-4" />
              <span>Focusing on <strong>{selectedCuisine.name}</strong> cuisine</span>
              <button 
                onClick={() => setSelectedCuisine(null)}
                className="ml-auto text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Clear
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Analysis Summary */}
      {orderAnalysis && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Order Analysis (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {orderAnalysis.total_orders || 0}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total Orders</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {orderAnalysis.unique_items_ordered || 0}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Unique Items</p>
              </div>
            </div>
            
            {orderAnalysis.top_by_volume && orderAnalysis.top_by_volume.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Top Sellers by Volume
                </h4>
                <div className="flex flex-wrap gap-2">
                  {orderAnalysis.top_by_volume.slice(0, 5).map(([name, count], index) => (
                    <Badge key={name} variant={index === 0 ? 'default' : 'outline'}>
                      {name}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Suggestions Output */}
      {suggestions && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Utensils className="h-5 w-5 text-primary" />
                AI-Generated Menu Suggestions
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => openQuickAdd()}
                  data-testid="quick-add-menu-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Add to Menu
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateSuggestions}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
              </div>
            </div>
            <CardDescription>
              These suggestions are generated by AI based on your order history and current menu.
              All suggestions are halaal-compliant. Click "Quick Add" to add any suggestion to your menu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-sm leading-relaxed bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                {suggestions}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!suggestions && !loading && (
        <Card className="border-slate-200 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <Brain className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Ready to Generate Menu Ideas
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              Click "Generate Ideas" to get AI-powered menu suggestions based on your order history
              and popular South African and international cuisines.
            </p>
            <Button
              onClick={generateSuggestions}
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Ideas
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">AI-Powered Suggestions</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Our AI analyzes your order history, popular items, and current trends to suggest
                new menu items that would work well for your halaal canteen. All suggestions 
                consider local South African tastes and corporate lunch budgets (R50-R150).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Add Dialog */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Quick Add Menu Item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="name">Item Name *</Label>
              <Input
                id="name"
                value={quickAddForm.name}
                onChange={(e) => setQuickAddForm({...quickAddForm, name: e.target.value})}
                placeholder="e.g., Cape Malay Chicken Curry"
                className="mt-1"
                data-testid="quick-add-name"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={quickAddForm.description}
                onChange={(e) => setQuickAddForm({...quickAddForm, description: e.target.value})}
                placeholder="Brief description of the dish..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price (R) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={quickAddForm.price}
                  onChange={(e) => setQuickAddForm({...quickAddForm, price: e.target.value})}
                  placeholder="95.00"
                  className="mt-1"
                  data-testid="quick-add-price"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={quickAddForm.category} 
                  onValueChange={(val) => setQuickAddForm({...quickAddForm, category: val})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Main Course">Main Course</SelectItem>
                    <SelectItem value="Side">Side</SelectItem>
                    <SelectItem value="Beverage">Beverage</SelectItem>
                    <SelectItem value="Dessert">Dessert</SelectItem>
                    <SelectItem value="Salad">Salad</SelectItem>
                    <SelectItem value="Soup">Soup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="menu_type">Menu Type</Label>
              <Select 
                value={quickAddForm.menu_type} 
                onValueChange={(val) => setQuickAddForm({...quickAddForm, menu_type: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily_meals">Daily Meals</SelectItem>
                  <SelectItem value="canteen">Canteen</SelectItem>
                  <SelectItem value="office_platters">Office Platters</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ingredients">Key Ingredients (comma-separated)</Label>
              <Input
                id="ingredients"
                value={quickAddForm.ingredients}
                onChange={(e) => setQuickAddForm({...quickAddForm, ingredients: e.target.value})}
                placeholder="chicken, rice, curry, spices"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setQuickAddOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitQuickAdd}
              disabled={addingItem || !quickAddForm.name || !quickAddForm.price}
              data-testid="quick-add-submit"
            >
              {addingItem ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Add to Menu
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
